import { FormEvent, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import WebApp from '@twa-dev/sdk'
import { apiClient } from '../api/client'
import { useEvent } from '../hooks/useEvents'
import { DetailErrorState, DetailLoadingState } from '../components/StateComponents'
import { getTelegramUserId, getTelegramUsername } from '../utils/telegram'
import { getActiveInteractionId, getAnalyticsContext, trackBusinessEvent } from '../utils/analytics'
import { useLanguage } from '../hooks/useLanguage'

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

interface ChatResponse {
  success: boolean
  reply?: string
  error?: string
}

interface SimpleInterpretResponse {
  event_id: string
  generated_at: string
  model: string
  interpretation: {
    raw_content?: string
    [key: string]: unknown
  }
  session_id?: string
  error?: string
}

function createChatTurnId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `chat_turn_${crypto.randomUUID()}`
  }
  return `chat_turn_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

export function ExplainPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const date = searchParams.get('date') || 'latest'
  const from = searchParams.get('from')
  const narrative = searchParams.get('narrative') || ''
  const index = searchParams.get('index')
  const total = searchParams.get('total')
  const { t } = useLanguage()

  const chatId = getTelegramUserId()
  const username = getTelegramUsername()

  const { data: event, isLoading: eventLoading, error: eventError } = useEvent(id, date)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isSending, setIsSending] = useState(false)

  const {
    data: interpretData,
    isLoading: interpretLoading,
    error: interpretError,
    refetch,
  } = useQuery({
    queryKey: ['explain-simple', id, date, chatId],
    queryFn: () => apiClient.post<SimpleInterpretResponse>(`/api/interpret/${id}/simple?date=${date}`, {
      user_id: chatId,
      username,
      analytics_context: getAnalyticsContext(),
    }),
    enabled: !!id && !!event,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const quickText = interpretData?.interpretation?.raw_content

  const handleBack = () => {
    if (from === 'detail' && id) {
      const params = new URLSearchParams({ date })
      if (narrative) params.set('narrative', narrative)
      if (index !== null) params.set('index', index)
      if (total !== null) params.set('total', total)

      navigate(`/event/${id}?${params.toString()}`)
      return
    }

    navigate(-1)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const question = input.trim()
    if (!question || !id || isSending) return

    setInput('')
    setMessages((prev) => [...prev, { role: 'user', content: question }])
    setIsSending(true)
    const startedAt = performance.now()
    const chatTurnId = createChatTurnId()
    const interactionId = getActiveInteractionId()
    const turnIndex = messages.filter((message) => message.role === 'user').length + 1
    const analyticsContext = {
      ...getAnalyticsContext(),
      interaction_id: interactionId,
      chat_turn_id: chatTurnId,
    }
    trackBusinessEvent('chat_send_click', {
      event_id: id,
      source_page: from === 'detail' ? 'event_detail' : 'home',
      chat_turn_id: chatTurnId,
      message_text: question,
      message_length: question.length,
      turn_index: turnIndex,
    }, id, interactionId)

    try {
      const response = await apiClient.post<ChatResponse>(`/api/chat/${id}`, {
        chat_id: chatId,
        message: question,
        chat_turn_id: chatTurnId,
        turn_index: turnIndex,
        analytics_context: analyticsContext,
      })

      if (response.success && response.reply) {
        setMessages((prev) => [...prev, { role: 'assistant', content: response.reply! }])
        WebApp.HapticFeedback.notificationOccurred('success')
        trackBusinessEvent('chat_send_success', {
          event_id: id,
          chat_turn_id: chatTurnId,
          message_text: question,
          message_length: question.length,
          turn_index: turnIndex,
          reply_length: response.reply.length,
          latency_ms: Math.round(performance.now() - startedAt),
        }, id, interactionId)
      } else {
        setMessages((prev) => [...prev, { role: 'assistant', content: response.error || t('追问失败，请先重新生成解读。', 'Follow-up failed. Please regenerate the explanation first.') }])
      }
    } catch (error) {
      console.error('Chat failed:', error)
      trackBusinessEvent('chat_send_fail', {
        event_id: id,
        chat_turn_id: chatTurnId,
        message_text: question,
        message_length: question.length,
        turn_index: turnIndex,
        latency_ms: Math.round(performance.now() - startedAt),
        error: error instanceof Error ? error.message : String(error),
      }, id, interactionId)
      setMessages((prev) => [...prev, { role: 'assistant', content: t('追问失败，请稍后重试。', 'Follow-up failed. Please try again later.') }])
    } finally {
      setIsSending(false)
    }
  }

  if (eventLoading) {
    return <DetailLoadingState />
  }

  if (eventError || !event) {
    return <DetailErrorState onBack={() => navigate('/')} />
  }

  return (
    <div className="min-h-screen bg-[var(--tg-bg-color,#f5f5f5)] pb-24">
      <header className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 z-10">
        <div className="flex items-center gap-3 p-3">
          <button
            onClick={handleBack}
            className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200"
            aria-label={t('返回', 'Back')}
          >
            ←
          </button>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{t('展开说说', 'Explain')}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{date}</div>
          </div>
        </div>
      </header>

      <main className="p-4 max-w-2xl mx-auto space-y-4">
        <section className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-700">
          <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-snug">
            {event.title || t('未命名事件', 'Untitled Event')}
          </h1>
          {(event.background || event.what_happened || event.summary) && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
              {event.background || event.what_happened || event.summary}
            </p>
          )}
        </section>

        <section className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('简短解读', 'Brief Explanation')}
            </h2>
            {interpretError && (
              <button
                onClick={() => refetch()}
                className="px-3 py-1 text-xs rounded-md bg-blue-500 text-white"
              >
                {t('重试', 'Retry')}
              </button>
            )}
          </div>

          {interpretLoading && (
            <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              {t('正在展开...', 'Generating explanation...')}
            </div>
          )}

          {interpretError && (
            <div className="py-4 text-sm text-red-600">
              {interpretError instanceof Error && (
                <div>{interpretError.message}</div>
              )}
              {t('解读生成失败，请稍后重试。', 'Failed to generate the explanation. Please try again later.')}
            </div>
          )}

          {quickText && (
            <div className="text-sm leading-relaxed text-gray-800 dark:text-gray-100 whitespace-pre-wrap">
              {quickText}
            </div>
          )}
        </section>

        <section className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
            {t('继续追问', 'Ask Follow-Up')}
          </h2>

          <div className="space-y-3">
            {messages.length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t(
                  '可以继续问：这件事有什么交易影响？还有哪些风险？后续应该跟踪什么信号？',
                  'You can ask: What are the trading implications? What risks remain? What signals should I track next?'
                )}
              </p>
            )}

            {messages.map((message, index) => (
              <div
                key={index}
                className={`rounded-lg p-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  message.role === 'user'
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100'
                    : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                }`}
              >
                {message.content}
              </div>
            ))}

            {isSending && (
              <div className="rounded-lg p-3 text-sm bg-gray-50 dark:bg-gray-700 text-gray-500">
                {t('正在思考...', 'Thinking...')}
              </div>
            )}
          </div>
        </section>
      </main>

      <form
        onSubmit={handleSubmit}
        className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-3 z-10"
      >
        <div className="max-w-2xl mx-auto flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={!interpretData || isSending}
            placeholder={interpretData ? t('继续追问...', 'Ask a follow-up...') : t('等待解读生成后追问', 'Wait for the explanation before asking')}
            className="flex-1 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-sm text-gray-900 dark:text-white outline-none disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={!input.trim() || !interpretData || isSending}
            className="px-4 py-2 rounded-lg bg-blue-500 text-white text-sm font-medium disabled:opacity-50"
          >
            {t('发送', 'Send')}
          </button>
        </div>
      </form>
    </div>
  )
}
